import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  LinearProgress,
  Pagination,
  Grid,
  Avatar,
  alpha,
  useTheme,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  ContactSupport,
  Add,
  MoreVert,
  Visibility,
  Edit,
  Close,
  PlayArrow,
  Pause,
  CheckCircle,
  Error,
  Warning,
  Info,
} from '@mui/icons-material';
import { Ticket } from '../../types';
import { ticketService } from '../../services/ticketService';
import { contractService } from '../../services/contractService';
import ChamadoForm from '../forms/ChamadoForm';
import ChamadosInfoView from '../modals/ChamadosInfoView';
import { toast } from 'react-toastify';

interface ContractTicketsProps {
  contractId: number;
  contractStatus: 'ativo' | 'bloqueado' | 'cancelado' | 'aguardando_instalacao' | 'aguardando_assinatura' | 'suspenso';
}

const ContractTickets: React.FC<ContractTicketsProps> = ({ contractId, contractStatus }) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTickets, setTotalTickets] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [chamadoFormOpen, setChamadoFormOpen] = useState(false);
  const [chamadoInfoViewOpen, setChamadoInfoViewOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [clienteId, setClienteId] = useState<number | null>(null);
  const theme = useTheme();

  useEffect(() => {
    loadContract();
  }, [contractId]);

  useEffect(() => {
    if (clienteId) {
      loadTickets();
    }
  }, [clienteId, page]);

  const loadContract = async () => {
    try {
      const response = await contractService.getById(contractId);
      if (response.success && response.data) {
        setClienteId(response.data.cliente_id);
      }
    } catch (err: any) {
      console.error('Erro ao carregar contrato:', err);
      setError('Erro ao carregar dados do contrato');
    }
  };

  const loadTickets = async () => {
    if (!clienteId) return;

    try {
      setLoading(true);
      setError(null);

      // Buscar tickets do cliente
      const response = await ticketService.getByClientId(clienteId, {
        page,
        limit: 10,
      });

      if (response.success && response.data) {
        // Filtrar tickets do contrato específico
        const contractTickets = response.data.filter(
          (ticket: Ticket) => ticket.contrato_id === contractId
        );

        setTickets(contractTickets);
        setTotalTickets(contractTickets.length);
        // Calcular total de páginas baseado nos tickets filtrados
        setTotalPages(Math.ceil(contractTickets.length / 10));
      }
    } catch (err: any) {
      console.error('Erro ao carregar tickets:', err);
      setError('Erro ao carregar chamados do contrato');
      toast.error('Erro ao carregar chamados');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, ticket: Ticket) => {
    setAnchorEl(event.currentTarget);
    setSelectedTicket(ticket);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTicket(null);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aberto':
        return 'error';
      case 'em_andamento':
        return 'warning';
      case 'resolvido':
        return 'success';
      case 'fechado':
        return 'default';
      case 'cancelado':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'aberto':
        return <Error />;
      case 'em_andamento':
        return <PlayArrow />;
      case 'aguardando_cliente':
        return <Pause />;
      case 'resolvido':
        return <CheckCircle />;
      case 'fechado':
        return <Close />;
      default:
        return <ContactSupport />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'aberto':
        return 'Aberto';
      case 'em_andamento':
        return 'Em Andamento';
      case 'aguardando_cliente':
        return 'Aguardando Cliente';
      case 'resolvido':
        return 'Resolvido';
      case 'fechado':
        return 'Fechado';
      default:
        return status;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgente':
        return 'error';
      case 'alta':
        return 'warning';
      case 'media':
        return 'info';
      case 'baixa':
        return 'success';
      default:
        return 'default';
    }
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'urgente':
        return 'Urgente';
      case 'alta':
        return 'Alta';
      case 'media':
        return 'Média';
      case 'baixa':
        return 'Baixa';
      default:
        return priority;
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'tecnico':
        return 'Técnico';
      case 'financeiro':
        return 'Financeiro';
      case 'comercial':
        return 'Comercial';
      case 'outros':
        return 'Outros';
      default:
        return category;
    }
  };

  const handleViewTicket = () => {
    if (selectedTicket) {
      setSelectedTicketId(selectedTicket.id);
      setChamadoInfoViewOpen(true);
    }
    handleMenuClose();
  };

  const handleChamadoInfoViewClose = () => {
    setChamadoInfoViewOpen(false);
    setSelectedTicketId(null);
  };

  const handleChamadoInfoViewSuccess = () => {
    loadTickets();
  };

  const handleEditTicket = () => {
    if (selectedTicket) {
      console.log('Editar ticket:', selectedTicket.id);
      // Implementar edição do ticket
    }
    handleMenuClose();
  };

  const handleNewTicket = () => {
    setSelectedTicketId(null);
    setChamadoFormOpen(true);
  };

  const handleChamadoFormClose = () => {
    setChamadoFormOpen(false);
    setSelectedTicketId(null);
  };

  const handleChamadoFormSuccess = () => {
    loadTickets();
    toast.success('Chamado criado com sucesso!');
  };

  if (loading) {
    return (
      <Box>
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Carregando chamados...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="h6" fontWeight="bold">
          Chamados do Contrato
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleNewTicket}
          disabled={contractStatus === 'cancelado'}
        >
          Novo Chamado
        </Button>
      </Box>

      {/* Resumo */}
      <Grid container spacing={2} mb={1.5}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Error color="error" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Abertos
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {tickets.filter(t => t.status === 'aberto').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PlayArrow color="warning" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Em Andamento
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {tickets.filter(t => t.status === 'em_andamento').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CheckCircle color="success" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Resolvidos
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {tickets.filter(t => t.status === 'resolvido').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ContactSupport color="primary" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {tickets.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabela de Tickets */}
      {tickets.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <ContactSupport sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhum chamado encontrado
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Este contrato ainda não possui chamados registrados
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleNewTicket}
                disabled={contractStatus === 'cancelado'}
              >
                Criar Primeiro Chamado
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <TableContainer
          component={Paper}
          elevation={2}
          sx={{
            borderRadius: 2,
            overflowX: 'auto',
            mb: 1,
            border: 'none'
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: 800,
              '& .MuiTableBody-root .MuiTableCell-root': {
                py: 0.6,
                fontSize: '0.78rem'
              }
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  '& .MuiTableCell-head': {
                    bgcolor: (t) => `color-mix(in srgb, ${theme.palette.primary.main} 12%, ${t.palette.background.paper})`,
                    fontWeight: 700,
                    fontSize: '0.73rem',
                    py: 0.8,
                    nowrap: 'nowrap',
                    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    whiteSpace: 'nowrap',
                    color: 'text.primary',
                  },
                }}
              >
                <TableCell sx={{ width: 80 }}>ID</TableCell>
                <TableCell>Assunto</TableCell>
                <TableCell sx={{ width: 100 }}>Prioridade</TableCell>
                <TableCell sx={{ width: 100 }}>Status</TableCell>
                <TableCell>Abertura</TableCell>
                <TableCell align="right" sx={{ width: 80 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((ticket) => (
                <TableRow
                  key={ticket.id}
                  hover
                  sx={{
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                    nowrap: 'nowrap',
                  }}
                  onClick={() => {
                    setSelectedTicketId(ticket.id);
                    setChamadoInfoViewOpen(true);
                  }}
                >
                  <TableCell sx={{ py: 0.6, px: 2 }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ fontSize: '0.78rem', color: 'primary.main', fontFamily: 'monospace' }}
                    >
                      #{ticket.id.toString().padStart(5, '0')}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.6, px: 2 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', lineHeight: 1.2 }}>
                      {ticket.assunto?.nome || 'Sem Assunto'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1, display: 'block', mt: 0.5 }}>
                      {ticket.descricao.substring(0, 100)}{ticket.descricao.length > 100 ? '...' : ''}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.6, px: 2 }}>
                    <Chip
                      label={getPriorityLabel(ticket.prioridade)}
                      color={getPriorityColor(ticket.prioridade) as any}
                      size="small"
                      sx={{ fontSize: '0.66rem', height: 20, fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.6, px: 2 }}>
                    <Chip
                      label={getStatusLabel(ticket.status)}
                      color={getStatusColor(ticket.status) as any}
                      size="small"
                      sx={{ fontSize: '0.66rem', height: 20, fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.6, px: 2 }}>
                    <Tooltip title={new Date(ticket.createdAt).toLocaleString()}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500 }}>
                          {new Date(ticket.createdAt).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                          {new Date(ticket.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.6, px: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTicketId(ticket.id);
                          setChamadoInfoViewOpen(true);
                        }}
                        disabled={contractStatus === 'cancelado'}
                      >
                        <Visibility fontSize="small" color="primary" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Paginação */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      )}

      {/* Menu de Ações */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleViewTicket}>
          <Visibility sx={{ mr: 1 }} />
          Visualizar
        </MenuItem>
        <MenuItem onClick={handleEditTicket}>
          <Edit sx={{ mr: 1 }} />
          Editar
        </MenuItem>
      </Menu>

      {/* Modal de Novo Chamado */}
      {clienteId && (
        <ChamadoForm
          open={chamadoFormOpen}
          onClose={handleChamadoFormClose}
          onSuccess={handleChamadoFormSuccess}
          clientId={clienteId}
          ticketId={selectedTicketId}
        />
      )}

      {/* Modal de Visualização de Chamado */}
      <ChamadosInfoView
        open={chamadoInfoViewOpen}
        onClose={handleChamadoInfoViewClose}
        ticketId={selectedTicketId}
        onSuccess={handleChamadoInfoViewSuccess}
      />
    </Box>
  );
};

export default ContractTickets; 